import { createInjectable } from "ngxtension/create-injectable";
import {
  HttpClient,
  HttpContext,
} from "@angular/common/http";
import { signal, computed, inject } from "@angular/core";
import {
  EntityData,
} from "../../../../core/interfaces/entity-data.interface";
import { EncryptStorage } from "encrypt-storage";
import { environment } from "environments/environment";
import { EntitiesDataV2ApiService } from "./entity-data-v2-api.service";
import { UserEntities } from "../../../../core/interfaces/user.interface";
import { AUTH_DAVESA_API } from "../../../../core/auth-davesa/auth-davesa-api.component";
import { AuthDavesaV2Service } from "../../../../core/auth-davesa/auth-davesa-v2.service";
import { EntityDataEnum } from "app/core/enum/entity-data.enum";

export const encryptStorage = new EncryptStorage(
  environment.ENCRYPT_STORAGE_KEY,
  {
    storageType: "sessionStorage",
  }
);

const ALL_USER_ROLES = "allUserRoles";
const ENTITY_DATA = "entityData";
const USER_ENTITY_DATA = "userEntityData";

const httpOptions = {
  context: new HttpContext().set(AUTH_DAVESA_API, true),
};

export const EntitiesDataV2Service = createInjectable(() => {
  const loginUser = inject(AuthDavesaV2Service).loginUser();
  const _entityDataV2ApiService = inject(EntitiesDataV2ApiService);
  const entityData = signal<EntityData[] | null>(null);
  const userEntityData = signal<UserEntities[] | null>(null);
  const allUserRoles = signal<any[] | null>(null);
  const allComplianceRoles = signal<any[] | null>(null);
  const allSpecialties = signal<any[] | null>(null);
  const allDelegationRoles = signal<any[] | null>(null);
  const allPrefix = signal<any[] | null>(null);
  const allSuffix = signal<any[] | null>(null);
  const allLocations = signal<any[] | null>(null);
  const allDocumentTypes = signal<any[] | null>(null);
  const allTrainingMethods = signal<any[] | null>(null);
  const allCountryCodes = signal<any[] | null>(null);
  const allSex = signal<any[] | null>(null);
  const allStudyTypes = signal<any[] | null>(null);
  const allStudyPhase = signal<any[] | null>(null);
  const allCurrency = signal<any[] | null>(null);
  const allEsourceFormCategory = signal<any[] | null>(null);
  const allContactCategories = signal<any[] | null>(null);
  const contactCategories = signal<any[] | null>(null);
  const contactCategory = signal<any | null>(null);

  const initiate = async (): Promise<boolean> => {
    try {
      loadFromStorage();
      return true;
    } catch (error) {
      console.error("Error signing in:", error);
      throw error;
    }
  };


  const loadFromStorage = () => {
    try {
      const jsonAllUserRoles = encryptStorage.getItem(ALL_USER_ROLES);
      allUserRoles.set(jsonAllUserRoles);
      const jsonEntityData = encryptStorage.getItem(ENTITY_DATA);
      entityData.set(jsonEntityData);
      const jsonUserEntityData = encryptStorage.getItem(USER_ENTITY_DATA);
      userEntityData.set(jsonUserEntityData);
    } catch (err) {
      return err;
    }

  };

  const setToStorage = () => {
    try {
      encryptStorage.setItem(ALL_USER_ROLES, JSON.stringify(allUserRoles()));
      encryptStorage.setItem(ENTITY_DATA, JSON.stringify(entityData()));
      encryptStorage.setItem(USER_ENTITY_DATA, JSON.stringify(userEntityData()));
    } catch (err) {
      return err;
    }
  };

  const removeFromStorage = () => {
    try {
      encryptStorage.removeItem(ALL_USER_ROLES);
      encryptStorage.removeItem(ENTITY_DATA);
      encryptStorage.removeItem(USER_ENTITY_DATA);
    } catch (err) {
      return err;
    }
  };

  const getAllUserRoles = async (): Promise<any> => {
      const response = await _entityDataV2ApiService.getAllUserRoles();
      allUserRoles.set(response);
      setToStorage();
      return response;  
  };

  const getAllEntitiesData = async (): Promise<any> => {
    if(loginUser) {
      const response = await _entityDataV2ApiService.getAllEntitiesData();
      entityData.set(response);
      setToStorage();
      return response;  
    }
    return null;
  };

  const getAllByEntity = async (): Promise<any> => {
    if(loginUser) {
      const response = await _entityDataV2ApiService.getAllByEntity();
      userEntityData.set(response);
      // let getAllDelegationRoles: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.DELEGATION_ROLES)
      // allDelegationRoles.set(getAllDelegationRoles[0].entityData);

      let getAllComplianceRoles: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.COMPLIANCE_ROLE)
      allComplianceRoles.set(getAllComplianceRoles[0].entityData);
      // console.log('getAllComplianceRoles', allComplianceRoles())

      let getAllSpecialties: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.SPECIALTY)
      allSpecialties.set(getAllSpecialties[0].entityData);
      // console.log('getAllSpecialties', allSpecialties())

      let getAllPrefix: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.PREFIX)
      allPrefix.set(getAllPrefix[0].entityData);
      // console.log('getAllPrefix', allPrefix())

      let getAllSuffix: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.SUFFIX)
      allSuffix.set(getAllSuffix[0].entityData);
      // console.log('getAllSuffix', allSuffix())

      let getAllLocations: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.LOCATION)
      allLocations.set(getAllLocations[0].entityData);

      let getAllDocumentTypes: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.DOCUMENT_TYPE)
      allDocumentTypes.set(getAllDocumentTypes[0].entityData);
      // console.log('getAllDocumentTypes', allDocumentTypes())

      let getAllTrainingMethods: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.TRAINING_METHOD)
      allTrainingMethods.set(getAllTrainingMethods[0].entityData);
      // console.log('getAllTrainingMethods', allTrainingMethods())

      let getAllCountryCodes: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.COUNTRY_CODE)
      allCountryCodes.set(getAllCountryCodes[0].entityData);

      let getAllSex: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.SEX)
      allSex.set(getAllSex[0].entityData);
      // console.log('getAllSex', allSex())

      let getAllStudyTypes: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.STUDY_TYPE)
      allStudyTypes.set(getAllStudyTypes[0].entityData);
      // console.log('getAllStudyTypes', allStudyTypes())

      let getAllStudyPhase: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.STUDY_PHASE)
      allStudyPhase.set(getAllStudyPhase[0].entityData);
      // console.log('getAllStudyPhase', allStudyPhase())

      let getAllCurrency: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.CURRENCY)
      allCurrency.set(getAllCurrency[0].entityData);
      // console.log('getAllCurrency', allCurrency())

      let getAllEsourceFormCategory: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.ESOURCE_FORM_CATEGORY)
      allEsourceFormCategory.set(getAllEsourceFormCategory[0].entityData);
      // console.log('getAllEsourceFormCategory', allEsourceFormCategory())

      let getAllContactCategory: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.CONTACT_CATEGORY)
      allContactCategories.set(getAllContactCategory[0].entityData);
      contactCategories.set(getAllContactCategory[0].entityData);
      // console.log('getAllContactCategory', allContactCategory())

      setToStorage();
      return response;
    }
    return null;
  };

  const createEntityItem = async (postData: EntityData): Promise<object> => {
    const response = await _entityDataV2ApiService.createEntityItem(postData);
    return response;
  };

  const updateEntityItem = async (postData: EntityData): Promise<object> => {
    const response = await _entityDataV2ApiService.updateEntityItem(postData);
    return response;
  };

  const deleteEntityItem = async (postData: EntityData): Promise<object> => {
    const response = await _entityDataV2ApiService.deleteEntityItem(postData);
    return response;
  };

  const setUserDetails = (data) => {
    entityData.set(data);
  };


    const searchContactCategories = async (query: string): Promise<any[]> => {
      try {
        let searchResults = allContactCategories().filter(
          (searchResults: any) =>
            searchResults.name &&
          searchResults.name.toLowerCase().includes(query.toLowerCase())
        );
        searchResults.sort((a: any, b: any) => a.name.localeCompare(b.name));
        contactCategories.set(searchResults);
        return searchResults;
      }
      catch (error) {
        console.error("Error in search:", error);
        throw error;
      }
    };
  
  return {
    entityData: computed(() => entityData()),
    userEntityData: computed(() => userEntityData()),
    allUserEntities: computed(() => userEntityData()),
    allUserRoles: computed(() => allUserRoles()),
    allComplianceRoles: computed(() => allComplianceRoles()),
    allSpecialties: computed(() => allSpecialties()),
    allDelegationRoles: computed(() => allDelegationRoles()),
    allPrefix: computed(() => allPrefix()),
    allSuffix: computed(() => allSuffix()),
    allLocations: computed(() => allLocations()),
    allDocumentTypes: computed(() => allDocumentTypes()),
    allTrainingMethods: computed(() => allTrainingMethods()),
    allCountryCodes: computed(() => allCountryCodes()),
    allSex: computed(() => allSex()),
    allStudyTypes: computed(() => allStudyTypes()),
    allStudyPhase: computed(() => allStudyPhase()),
    allContactCategories: computed(() => allContactCategories()),
    contactCategories: computed(() => contactCategories()),
    contactCategory: computed(() => contactCategory()),
    initiate,
    loadFromStorage,
    setToStorage,
    removeFromStorage,
    setUserDetails,
    getAllUserRoles,
    getAllEntitiesData,
    getAllByEntity,
    createEntityItem,
    updateEntityItem,
    deleteEntityItem,
    searchContactCategories
  };
});
